
















































































































































import { mapState } from "vuex";
import { Component, Prop, Watch, Mixins } from "vue-property-decorator";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import HasValidation from "@/mixins/HasValidation";
import { ValidationObserver } from "vee-validate";

// Enums
import { ProductTypes, OrderTypes } from "@/data/enums/products";
import { ActionTypes } from "@/data/enums";
import { SortableItems } from "@/data/enums/sortableItems";
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import {
  IProduct,
  IProductCategory,
  IProductForm,
  ISelectedProduct
} from "@/models/products";
import { IBrand } from "@/models/brands";

@Component({
  components: {
    "multilevel-categories-selections": () =>
      import("./multilevelCategories/multilevelCategoriesSelections.vue"),
    "product-is-active-checkbox": () => import("./productIsActiveCheckbox.vue"),
    "product-is-visible-checkbox": () =>
      import("./productIsVisibleCheckbox.vue"),
    "category-picker": () => import("./categoryPicker.vue")
  },
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.brand.id;
      },
      productsScope(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/productsScope`](
          this.brandId,
          this.form[this.getFormCategoryField],
          this.productTypes
        );
      },
      params(state: IState, getters) {
        return getters[`data/${DataModules.CATALOGUE}/listParams`]();
      }
    })
  }
})
export default class AddProductModal extends Mixins(
  RequestPromises,
  HasValidation
) {
  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    name: HTMLFormElement;
  };

  // :getters
  public brandId!: IBrand["id"];
  public productsScope!: string;
  public params!: object;

  @Prop({ required: true })
  public productTypes!: ProductTypes[];

  @Prop({ default: null })
  public categoryId!: IProductCategory["id"];

  @Prop({ default: () => ({}) })
  public extraProps!: any;

  @Prop({ default: true })
  public redirect!: boolean;

  public form = {} as IProductForm;
  public categoriesModule: string = DataModules.CATALOGUE_CATEGORIES;

  public isLoading = false;
  public isReloading = false;
  public isProcessing = false;
  public requestError = false;

  public savedCategoryId = 0;

  public selectedProducts: ISelectedProduct[] = [];

  public get productTypesEnums() {
    return ProductTypes;
  }

  public get isBundle() {
    return this.form.product_type === ProductTypes.PRODUCT_BUNDLE;
  }

  public created() {
    this.fillForm();
  }

  public fillForm() {
    this.form = {
      brand_id: this.brandId,
      name: "",
      description: "",
      available_for_sales: 1,
      clients_can_order: 1,
      products_category_id: null,
      products_options_category_id: null,
      products_attributes_category_id: null,
      product_type:
        this.getProductType === "option"
          ? ProductTypes.PRODUCT_OPTION
          : this.getProductType === "attribute"
          ? ProductTypes.PRODUCT_ATTRIBUTE
          : ProductTypes.SINGLE_PRODUCT,
      contract_type: 0,
      billing_cycle_months: 0,
      order_type: this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
        ? OrderTypes.CONFIGURATION_BASED
        : OrderTypes.SINGLE_OPTION,
      unit_quantity: 1,
      currency_id: "",
      set_products: [],
      set_order_type: 0,
      set_price_type: 1
    };

    if (this.categoryId) {
      this.form[this.getFormCategoryField] = this.categoryId;
    }
  }

  public get getFormCategoryField() {
    return `products${
      this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
        ? "_options"
        : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
        ? "_attributes"
        : ""
    }_category_id`;
  }

  public get getProductType() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
      ? "option"
      : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
      ? "attribute"
      : "product";
  }

  public getTypeName(ending: string) {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
      ? `_option${ending}`
      : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
      ? `_attribute${ending}`
      : ending;
  }

  public get formIsValid() {
    if (this.$_.isEmpty(this.form.name)) {
      return false;
    }

    if (this.$_.isNull(this.form[this.getFormCategoryField])) {
      return false;
    }

    if (
      this.form.product_type === ProductTypes.PRODUCT_BUNDLE &&
      !this.form.set_products.length
    ) {
      return false;
    }

    return true;
  }

  public get formIsDisabled() {
    return (
      this.isProcessing || !this.formIsValid || this.hasErrors || this.isLoading
    );
  }

  public async validateBeforeSubmit() {
    if (!this.canCreateProduct) return;
    try {
      this.isProcessing = true;
      const result = await this.validate();
      if (!result) throw this.$t("_sentence.form_is_not_valid");
      const product: IProduct = await this.createProduct();
      this.doRedirect(product);
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_added", {
          object: this.$t(
            `_.product${
              this.form.product_type === ProductTypes.PRODUCT_BUNDLE
                ? "_bundle"
                : this.getTypeName("")
            }`
          )
        }) as string
      });
      this.$emit("success", product);
      this.$emit("close");
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public doRedirect({ id, product_type }: IProduct) {
    if (!this.redirect) return;

    return this.$router
      .push({
        name:
          product_type === ProductTypes.PRODUCT_OPTION
            ? "adminCatalogueOption"
            : product_type === ProductTypes.PRODUCT_ATTRIBUTE
            ? "adminCatalogueAttribute"
            : "adminCatalogueProduct",
        params: {
          productId: id
        }
      })
      .catch(err => err);
  }

  public createProduct() {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/create`, {
      data: this.$_.merge(
        {},
        this.$_.pick(this.form, [
          "name",
          "description",
          this.getFormCategoryField,
          "product_type",
          "contract_type",
          "available_for_sales",
          "clients_can_order",
          "billing_cycle_months",
          "order_type",
          "currency_id",
          "brand_id"
        ]),
        this.form.product_type === ProductTypes.PRODUCT_BUNDLE
          ? this.$_.pick(this.form, [
              "set_products",
              "set_order_type",
              "set_price_type"
            ])
          : {},
        this.extraProps
      ),
      scope: this.productsScope
    });
  }

  public async onCategoryChange(value: any) {
    if (value === "new") {
      this.$nextTick(() => {
        this.$set(this.form, this.getFormCategoryField, this.savedCategoryId);
      });

      const result = await this.$store.dispatch(
        `data/${DataModules.CATALOGUE_CATEGORIES}/openAddEditCategoryModal`,
        {
          productTypes: this.productTypes,
          brandId: this.form.brand_id,
          actionType: ActionTypes.CREATE
        }
      );

      this.form[this.getFormCategoryField] = this.$_.get(
        result,
        "id",
        this.savedCategoryId
      );
    } else {
      this.savedCategoryId = value;
    }
  }

  public get canCreateCategory() {
    return this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
      ? this.$userCan(`create_product_options_category`)
      : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
      ? this.$userCan(`create_product_attributes_category`)
      : this.$userCan(`create_product_category`);
  }

  public get canCreateProduct() {
    return this.$userCan("create_product");
  }

  public detachItem(itemId: IProduct["id"]) {
    this.selectedProducts = this.$_.filter(
      this.selectedProducts,
      selectedProduct => selectedProduct.id !== itemId
    );
  }

  public onSelectedProducts(products) {
    this.selectedProducts = this.$_.map(products, product => {
      product.type = SortableItems.ITEM;
      return product;
    });
  }

  @Watch("selectedProducts")
  public onSelectedProductsChanged() {
    this.form.set_products = this.$_.map(this.selectedProducts, product => {
      return { product_id: product.id };
    });
  }

  public guardFunctionalities() {
    return ["create_product"];
  }

  public get contentAvailable() {
    return (
      this.$userCan("create_product") &&
      (this.productTypes.includes(ProductTypes.PRODUCT_OPTION)
        ? this.$userCan("list_product_options_categories")
        : this.productTypes.includes(ProductTypes.PRODUCT_ATTRIBUTE)
        ? this.$userCan("list_product_attributes_categories")
        : this.$userCan("list_product_categories"))
    );
  }
}
